// Analyse Trade payoff chart (every option strategy: SELL pairs and Dynamic Adaptive Rescue, Iron Condor, Box Spread,
// BUY pairs). GREEN = theoretical P&L AT EXPIRY from confirmed fills (exact). BLUE = model-projected P&L at a chosen
// date before expiry (an estimate, `projected` on the curve points). Rupees when the broker units are verified, else
// per unit. Focus on position (default) / Full risk range, zoom, pan and reset; marker labels are laid out in lanes
// above the plot so they never overlap. Display only - nothing here feeds an order, a trigger or a risk gate.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Area, CartesianGrid, ComposedChart, Line, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { rupees } from "@/components/options/PairValuation";
import { focusRange, fullRange, layoutLabels, panRange, visibleY, zoomRange } from "@/lib/payoffView";

const GREEN = "#00C805";
const RED = "#FF3B30";
const BLUE = "#2563EB";
const GOLD = "#b38f00";
const Y_W = 76, M_L = 8, M_R = 16, LANE_H = 13;

export function chartPoints(payoff) {
  const rupee = (payoff?.curve || []).every((p) => p.pnl !== null && p.pnl !== undefined);
  const today = new Map((payoff?.today || []).map((p) => [p.s, rupee ? p.pnl : p.pnl_unit]));
  return {
    rupee,
    hasToday: today.size > 0 || (payoff?.curve || []).some((p) => p.projected != null),
    points: (payoff?.curve || []).map((p) => {
      const v = rupee ? p.pnl : p.pnl_unit;
      const t = p.projected != null ? p.projected : today.has(p.s) ? today.get(p.s) : null;
      return { s: p.s, pnl: v, profit: v > 0 ? v : 0, loss: v < 0 ? v : 0, today: t };
    }),
  };
}

/** The chart's own default markers: a condor's K1..K4, breakevens and spot (when the caller passes none). */
function defaultMarkers(payoff, markers) {
  const out = [];
  if (markers) out.push(...markers.map((m) => ({ kind: "custom", color: BLUE, ...m })));
  else {
    const [k1, k2, k3, k4] = payoff.strikes || [];
    [k1, k4].forEach((k) => k != null && out.push({ x: k, label: `BUY ${k}`, color: GREEN, kind: "strike" }));
    [k2, k3].forEach((k) => k != null && out.push({ x: k, label: `SELL ${k}`, color: RED, kind: "strike" }));
  }
  (payoff.breakevens || []).forEach((b) => b != null && out.push({ x: Number(b), label: `BE ${Number(b).toLocaleString("en-IN", { maximumFractionDigits: 1 })}`, color: GOLD, kind: "be" }));
  if (payoff.spot != null && !(markers || []).some((m) => m.kind === "spot")) out.push({ x: Number(payoff.spot), label: `Spot ${Number(payoff.spot).toLocaleString("en-IN")}`, color: BLUE, kind: "spot", solid: true });
  return out;
}

function useWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(600);
  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((e) => setW(e[0].contentRect.width || 600));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

const btn = "px-1.5 py-0.5 rounded-sm border border-[var(--term-border)] hover:border-[var(--term-primary)] text-[10px] leading-none";

/** `markers` ([{x, label, color, kind, dash, solid}]) replaces the condor's strike lines; `focusPoints` (prices)
 *  defines the Focus window (default: strikes, breakevens, spot, markers); `bands` ([{x1, x2, color, label}]) shades
 *  regions (e.g. rescue scan zones). `projectedLabel` names the blue line. */
export default function IcPayoffChart({ payoff, height = 280, markers = null, note = null, focusPoints = null, bands = null,
  projectedLabel = "Projected P&L (estimate)", testId = "ic-payoff-chart", onPick = null }) {
  const { rupee, points, hasToday } = useMemo(() => chartPoints(payoff), [payoff]);
  const allMarkers = useMemo(() => (points.length ? defaultMarkers(payoff, markers) : []), [payoff, markers, points.length]);
  const bounds = useMemo(() => (points.length ? [points[0].s, points[points.length - 1].s] : null), [points]);
  const focus = useMemo(() => {
    if (!bounds) return null;
    const pts = focusPoints || [...(payoff.strikes || []), ...(payoff.breakevens || []), payoff.spot, ...allMarkers.map((m) => m.x)];
    const f = focusRange(pts) || bounds;
    return [Math.max(bounds[0], f[0]), Math.min(bounds[1], f[1])];
  }, [bounds, focusPoints, payoff.strikes, payoff.breakevens, payoff.spot, allMarkers]);
  const full = useMemo(() => bounds, [bounds]);
  const [view, setView] = useState("focus");
  const [win, setWin] = useState(null);
  const key = focus ? `${focus[0]}|${focus[1]}` : "";
  const lastKey = useRef(key);
  useEffect(() => {          // a new position (e.g. a rescue fill) re-focuses unless the user zoomed by hand
    if (lastKey.current !== key && view !== "manual") setWin(null);
    lastKey.current = key;
  }, [key, view]);
  const [ref, width] = useWidth();
  if (!points.length) return <div className="text-xs text-[var(--term-muted)] p-4">No payoff available.</div>;
  const domain = win || (view === "full" ? full : focus);
  const yDom = visibleY(points, domain, hasToday ? ["pnl", "today"] : ["pnl"]);
  const fmt = (v) => (rupee ? rupees(v) : `${Number(v).toFixed(2)} / unit`);
  const plotW = Math.max(100, width - Y_W - M_L - M_R);
  const laid = layoutLabels(allMarkers, domain, plotW);
  const lanes = laid.reduce((a, m) => Math.max(a, m.lane + 1), 0);
  const edge = (x) => points.reduce((best, p) => (Math.abs(p.s - x) < Math.abs(best.s - x) ? p : best), points[0]);
  const leftOut = domain[0] > bounds[0] + 1e-9 ? edge(bounds[0]) : null;
  const rightOut = domain[1] < bounds[1] - 1e-9 ? edge(bounds[1]) : null;
  const setManual = (r) => { setWin(r); setView("manual"); };
  return (
    <div data-testid={testId} className="min-w-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-[var(--term-muted)] pb-1">
        <span><span style={{ color: GREEN }}>━</span> P&amp;L at expiry {rupee ? "(₹, from fills, before exit charges)" : "(per price unit — broker units not yet verified)"}</span>
        {hasToday && <span style={{ color: BLUE }} data-testid={`${testId}-proj-legend`}>╍ {projectedLabel}</span>}
        {note && <span>{note}</span>}
        <span className="ml-auto flex items-center gap-1" data-testid={`${testId}-controls`}>
          <button type="button" className={btn} data-active={view === "focus" && !win} onClick={() => { setWin(null); setView("focus"); }}
            style={view === "focus" && !win ? { borderColor: BLUE, color: BLUE } : undefined} data-testid={`${testId}-focus`}>Focus on position</button>
          <button type="button" className={btn} onClick={() => { setWin(null); setView("full"); }}
            style={view === "full" && !win ? { borderColor: BLUE, color: BLUE } : undefined} data-testid={`${testId}-full`}>Full risk range</button>
          <button type="button" className={btn} title="Zoom in" aria-label="Zoom in" onClick={() => setManual(zoomRange(domain, 0.7, { center: payoff.target ?? payoff.spot, bounds }))} data-testid={`${testId}-zoom-in`}>＋</button>
          <button type="button" className={btn} title="Zoom out" aria-label="Zoom out" onClick={() => setManual(zoomRange(domain, 1.4, { center: payoff.target ?? payoff.spot, bounds }))} data-testid={`${testId}-zoom-out`}>－</button>
          <button type="button" className={btn} title="Pan left" aria-label="Pan left" onClick={() => setManual(panRange(domain, -0.2, bounds))} data-testid={`${testId}-pan-left`}>◀</button>
          <button type="button" className={btn} title="Pan right" aria-label="Pan right" onClick={() => setManual(panRange(domain, 0.2, bounds))} data-testid={`${testId}-pan-right`}>▶</button>
          <button type="button" className={btn} title="Reset to the focused view" onClick={() => { setWin(null); setView("focus"); }} data-testid={`${testId}-reset`}>Reset</button>
        </span>
      </div>
      <div ref={ref} className="relative" style={{ paddingTop: lanes * LANE_H }} data-testid={`${testId}-plot`} data-domain={`${Math.round(domain[0])}-${Math.round(domain[1])}`}>
        <div className="absolute top-0 pointer-events-none" style={{ left: Y_W + M_L, width: plotW, height: lanes * LANE_H }}>
          {laid.map((m) => (
            <span key={`${m.kind}${m.x}${m.label}`} data-testid={`${testId}-label`} data-lane={m.lane} title={m.title || m.label}
              className="absolute whitespace-nowrap text-[9px] leading-none mono" style={{ left: m.left, top: (lanes - 1 - m.lane) * LANE_H, color: m.color }}>{m.label}</span>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart data={points} margin={{ top: 4, right: M_R, bottom: 4, left: M_L }} onClick={(e) => onPick && e?.activeLabel != null && onPick(Number(e.activeLabel))}>
            <CartesianGrid stroke="var(--term-border)" strokeDasharray="3 3" />
            <XAxis dataKey="s" type="number" domain={domain} allowDataOverflow tick={{ fontSize: 10 }} stroke="var(--term-muted)"
              tickFormatter={(v) => Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })} tickCount={7} />
            <YAxis tick={{ fontSize: 10 }} stroke="var(--term-muted)" width={Y_W} domain={yDom} allowDataOverflow
              tickFormatter={(v) => (rupee ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : Number(v).toFixed(1))} />
            <Tooltip formatter={(v, name) => (name === "pnl" ? [fmt(v), "P&L at expiry"] : name === "today" ? [fmt(v), projectedLabel] : [null, null])}
              labelFormatter={(s) => `Underlying ${Number(s).toLocaleString("en-IN")}`}
              contentStyle={{ background: "var(--term-panel)", border: "1px solid var(--term-border-strong)", fontSize: 11 }} />
            {(bands || []).map((b) => <ReferenceArea key={`band${b.x1}${b.x2}`} x1={b.x1} x2={b.x2} fill={b.color} fillOpacity={0.08} ifOverflow="hidden" />)}
            <Area type="linear" dataKey="profit" stroke="none" fill={GREEN} fillOpacity={0.15} isAnimationActive={false} />
            <Area type="linear" dataKey="loss" stroke="none" fill={RED} fillOpacity={0.15} isAnimationActive={false} />
            <Line type="linear" dataKey="pnl" stroke={GREEN} strokeWidth={2} dot={false} isAnimationActive={false} />
            {hasToday && <Line type="monotone" dataKey="today" stroke={BLUE} strokeWidth={1.75} strokeDasharray="6 3" dot={false} isAnimationActive={false} connectNulls />}
            <ReferenceLine y={0} stroke="var(--term-muted)" />
            {allMarkers.map((m) => <ReferenceLine key={`l${m.kind}${m.x}${m.label}`} x={m.x} stroke={m.color} strokeWidth={m.solid ? 2 : 1}
              strokeDasharray={m.solid ? undefined : m.dash || "2 3"} ifOverflow="hidden" />)}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {(leftOut || rightOut) && (
        <div className="flex justify-between text-[10px] text-[var(--term-muted)] pt-0.5" data-testid={`${testId}-outside`}>
          <span>{leftOut ? <>◀ outside this view: {fmt(leftOut.pnl)} at {Number(leftOut.s).toLocaleString("en-IN")}{payoff.lowerTail ? ` · ${payoff.lowerTail}` : ""}</> : ""}</span>
          <span>{rightOut ? <>{fmt(rightOut.pnl)} at {Number(rightOut.s).toLocaleString("en-IN")}{payoff.upperTail ? ` · ${payoff.upperTail}` : ""} ▶</> : ""}</span>
        </div>
      )}
    </div>
  );
}
